import React, { useContext, useState, useEffect, useMemo } from 'react';
import './Payment.css';
import { PaymentContext } from "../../context/PaymentContext.js";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import Loader from '../../components/Loader.js';
import { useToast } from '../../context/ToastContext.js';
import { AuthContext } from '../../context/AuthContext.js';
import {
  validateCardForm,
  formatExpiryDate,
  validateTCKN
} from '../../lib/validators.js';
import countryCodesData from '../../constants/countryCodes.json';
import CountrySelector from '../../components/CountrySelector';

Modal.setAppElement('#root');

const Payment = () => {
  const {
    state: { cardNumber, totalAmount, cardHolderName, expiryDate, cvc, installments, commissionRate, selectedInstallment, error, paymentSuccess },
    setCardNumber,
    setCardHolderName,
    setExpiryDate,
    setCvc,
    setTotalAmount,
    fetchCommissionRates,
    fetchInstallments,
    setSelectedInstallment,
    processPayment,
    calculateMonthlyPayment,
    confirmEftPayment
  } = useContext(PaymentContext);
  const location = useLocation();
  const { total, customerForm: initialCustomerForm, cart } = location.state || {};
  const [customerForm, setCustomerForm] = useState(initialCustomerForm || {});
  const [isOpen, setIsOpen] = useState(false);
  const [isFormActive, setIsFormActive] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedInstallmentData, setSelectedInstallmentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Edit mode states
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [editedInvoiceData, setEditedInvoiceData] = useState({});

  // Country selector state
  const countryCodes = useMemo(() => {
    const turkey = countryCodesData.find(c => c.code === 'TR');
    const others = countryCodesData.filter(c => c.code !== 'TR').sort((a, b) => a.name.localeCompare(b.name));
    return [turkey, ...others];
  }, []);

  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);

  // Kart animasyonu için state'ler
  const [isCvcFocused, setIsCvcFocused] = useState(false);

  // EFT/Havale ödemesi için state'ler
  const [orderCreated, setOrderCreated] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderAmount, setOrderAmount] = useState(0);
  const [copySuccess, setCopySuccess] = useState('');
  const [eftData, setEftData] = useState(null);
  const [eftDiscount, setEftDiscount] = useState(0); // EFT indirimi için

  const [paymentState, setPaymentState] = useState({
    success: false,
    orderId: null,
    amount: null
  });

  const [isPaymentCompleteModalOpen, setIsPaymentCompleteModalOpen] = useState(false);
  const authContext = useContext(AuthContext);
  const isLoggedIn = useMemo(() => {
    const contextToken = authContext?.token;
    return Boolean(contextToken || localStorage.getItem('token'));
  }, [authContext?.token]);
  const invoiceFieldMapping = useMemo(() => ({
    ad: 'faturaAd',
    soyad: 'faturaSoyad',
    tckimlik: 'faturaTckimlik',
    telefon: 'faturaTelefon',
    dogumTarihi: 'faturaDogumTarihi',
    adres: 'faturaAdres',
    email: 'faturaEmail'
  }), []);

  const isEftPayment = customerForm && customerForm.paymentType === 'eft';

  // Sarrafiye ürünleri kontrolü
  const hasSarrafiyeProducts = cart && cart.some(product => product.sarrafiye == 1);

  const formatPhoneForDisplay = (number = '') => {
    const digits = number.replace(/\D/g, '');
    if (digits.length !== 11) return digits || '-';
    return digits.replace(/(\d{4})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
  };

  const formatDateForDisplay = (value = '') => {
    if (!value) return '-';

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-');
      return `${day}.${month}.${year}`;
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      const day = `${parsed.getDate()}`.padStart(2, '0');
      const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
      const year = `${parsed.getFullYear()}`;
      return `${day}.${month}.${year}`;
    }

    return value;
  };

  const billingInfo = useMemo(() => {
    if (!customerForm) return {};

    const billingAddress =
      customerForm.faturaAdresi ||
      (customerForm.faturamAyniAdres ? customerForm.teslimatAdresi : null);

    const resolvedAddress = billingAddress || customerForm.teslimatAdresi || {};

    return {
      ad: customerForm.faturaAd ?? resolvedAddress.ad ?? customerForm.ad ?? '',
      soyad: customerForm.faturaSoyad ?? resolvedAddress.soyad ?? customerForm.soyad ?? '',
      tckimlik: customerForm.faturaTckimlik ?? resolvedAddress.tc ?? '',
      telefon: customerForm.faturaTelefon ?? resolvedAddress.telefon ?? customerForm.telefon ?? '',
      dogumTarihi: customerForm.faturaDogumTarihi ?? resolvedAddress.dogum_tarihi ?? '',
      adres: customerForm.faturaAdres ?? resolvedAddress.addressText ?? customerForm.adres ?? '',
      il: customerForm.faturaIl ?? resolvedAddress.il ?? customerForm.il ?? '',
      ilce: customerForm.faturaIlce ?? resolvedAddress.ilce ?? customerForm.ilce ?? '',
      email: customerForm.faturaEmail ?? resolvedAddress.email ?? customerForm.email ?? ''
    };
  }, [customerForm]);

  const billingAddressLine = useMemo(() => {
    const parts = [];
    if (billingInfo.adres) {
      parts.push(billingInfo.adres);
    }
    const cityParts = [billingInfo.ilce, billingInfo.il].filter(Boolean).join(', ');
    if (cityParts) {
      parts.push(cityParts);
    }
    return parts.join(' • ');
  }, [billingInfo.adres, billingInfo.il, billingInfo.ilce]);

  const handleFocus = () => setIsFormActive(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (total) {
      setTotalAmount(total);

      if (isEftPayment) {
        const discountAmount = total * 0.02;
        setEftDiscount(discountAmount);
      }
    }
    fetchCommissionRates();
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setIsEditingInvoice(true);
      setEditedInvoiceData(prev => {
        if (Object.keys(prev || {}).length > 0) {
          return prev;
        }
        return {
          ad: '',
          soyad: '',
          tckimlik: '',
          telefon: '',
          dogumTarihi: '',
          adres: '',
          email: ''
        };
      });
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (cardNumber.length == 8 || cardNumber.length == 16) {
      fetchInstallments(cardNumber);
    } else {
      setSelectedInstallment(null);
    }
  }, [cardNumber]);

  useEffect(() => {
    const fullName = `${billingInfo.ad || ''} ${billingInfo.soyad || ''}`.trim();
    if (fullName && fullName !== cardHolderName) {
      setCardHolderName(fullName);
    }
  }, [billingInfo.ad, billingInfo.soyad, cardHolderName, setCardHolderName]);

  // Taksit seçenekleri yüklenmediyse varsayılan 1 taksit seç
  useEffect(() => {
    if (cardNumber.length >= 8 && (!installments || installments.length === 0) && !selectedInstallment && !isEftPayment) {
      setSelectedInstallment(1);
      setSelectedInstallmentData({ count: 1, rate: 0, totalAmount: total });
    }
  }, [cardNumber, installments, selectedInstallment, isEftPayment, total]);

  // EFT verilerini getir
  useEffect(() => {
    if (isEftPayment && orderCreated) {
      const fetchEftData = async () => {
        try {
          const response = await fetch('https://api.bikuyum.com/api/get_eft');
          const data = await response.json();
          if (data && data.length > 0) {
            setEftData(data[0]);
          }
        } catch (error) {
          showToast('EFT bilgileri şu anda alınamıyor', 'error')
          console.error('EFT bilgileri alınırken hata oluştu:', error);
        }
      };

      fetchEftData();
    }
  }, [isEftPayment, orderCreated]);

  // Kredi kartı validasyon fonksiyonu, artık validators.js'den alınacak
  const validateForm = () => {
    return validateCardForm({
      cardNumber,
      cardHolderName,
      expiryDate,
      cvc,
      selectedInstallmentData
    });
  };

  const handleCardNumberChange = (e) => {
    let newCardNumber = e.target.value.replace(/\D/g, '');
    if (newCardNumber.length > 0) {
      newCardNumber = newCardNumber.slice(0, 16);
    }
    setCardNumber(newCardNumber);
    // Hata mesajını temizle, validasyon sadece form gönderiminde yapılacak
    setErrors(prev => ({ ...prev, cardNumber: '' }));
  };

  const handleInstallmentChange = (e) => {
    const selectedValue = Number(e.target.value);
    setSelectedInstallment(selectedValue);
    const selected = installments.find(inst => inst.count === selectedValue);
    setSelectedInstallmentData(selected);
    setErrors(prev => ({ ...prev, installment: '' }));
  };

  const handleCardHolderNameChange = (e) => {
    setCardHolderName(e.target.value);
    // Hata mesajını temizle, validasyon sadece form gönderiminde yapılacak
    setErrors(prev => ({ ...prev, cardHolderName: '' }));
  };

  const handleExpiryDateChange = (e) => {
    const formattedDate = formatExpiryDate(e.target.value);
    setExpiryDate(formattedDate);
    // Hata mesajını temizle, validasyon sadece form gönderiminde yapılacak
    setErrors(prev => ({ ...prev, expiryDate: '' }));
  };

  useEffect(() => {
    if (total) {
      setTotalAmount(total);
    }
  }, [total]);

  const handleCvcChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 3);
    setCvc(value);
    setErrors(prev => ({ ...prev, cvc: '' }));
  };

  // Fatura Düzenleme Fonksiyonları
  const handleEditClick = () => {
    setEditedInvoiceData({
      ad: billingInfo.ad,
      soyad: billingInfo.soyad,
      tckimlik: billingInfo.tckimlik,
      telefon: billingInfo.telefon,
      dogumTarihi: billingInfo.dogumTarihi,
      adres: billingInfo.adres,
      email: billingInfo.email
    });
    setIsEditingInvoice(true);
  };

  const handleInvoiceChange = (e) => {
    const { name, value } = e.target;

    setEditedInvoiceData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'ad' || name === 'soyad') {
      const newAd = name === 'ad' ? value : editedInvoiceData.ad || '';
      const newSoyad = name === 'soyad' ? value : editedInvoiceData.soyad || '';
      const fullName = `${newAd} ${newSoyad}`.trim();
      setCardHolderName(fullName);
    }

    const mappedKey = invoiceFieldMapping[name];
    if (mappedKey) {
      setCustomerForm(prev => ({
        ...(prev || {}),
        [mappedKey]: value
      }));
    }
  };

  const handlePhoneChange = (e) => {
    const { value } = e.target;
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    const maxLength = selectedCountry.mobile_number_length || 15;

    if (sanitizedValue.length > maxLength) {
      return;
    }

    setEditedInvoiceData(prev => ({
      ...prev,
      telefon: sanitizedValue
    }));

    const mappedKey = invoiceFieldMapping['telefon'];
    if (mappedKey) {
      setCustomerForm(prev => ({
        ...(prev || {}),
        [mappedKey]: sanitizedValue
      }));
    }
  };

  const handleSaveInvoice = () => {
    // Basit validasyon
    if (!editedInvoiceData.ad || !editedInvoiceData.soyad || !editedInvoiceData.tckimlik || !editedInvoiceData.telefon || !editedInvoiceData.adres) {
      showToast('Lütfen zorunlu alanları doldurunuz.', 'error');
      return;
    }

    if (!validateTCKN(editedInvoiceData.tckimlik)) {
      showToast('Geçersiz TC Kimlik Numarası.', 'error');
      return;
    }

    setCustomerForm(prev => ({
      ...prev,
      faturaAd: editedInvoiceData.ad,
      faturaSoyad: editedInvoiceData.soyad,
      faturaTckimlik: editedInvoiceData.tckimlik,
      faturaTelefon: editedInvoiceData.telefon,
      faturaDogumTarihi: editedInvoiceData.dogumTarihi,
      faturaAdres: editedInvoiceData.adres,
      faturaEmail: editedInvoiceData.email
    }));
    setIsEditingInvoice(false);
    showToast('Fatura bilgileri güncellendi.', 'success');
  };

  const handleCancelEdit = () => {
    setIsEditingInvoice(false);
    setEditedInvoiceData({});
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    // Taksit seçimi kontrolü - API'den taksit yüklenmediyse varsayılan 1 taksit kullan
    if (!isEftPayment && !selectedInstallment) {
      // Eğer installments dizisi boşsa veya yüklenmediyse, varsayılan 1 taksit seç
      if (!installments || installments.length === 0) {
        setSelectedInstallment(1);
        setSelectedInstallmentData({ count: 1, rate: 0, totalAmount: total });
      } else {
        setErrors(prev => ({ ...prev, submit: 'Taksit seçmediniz. Lütfen taksit seçeneğini belirtiniz.' }));
        showToast('Lütfen taksit seçeneğini belirtiniz.', 'error');
        return;
      }
    }

    // Kredi kartı için doğrulama yap
    if (!isEftPayment) {
      const formErrors = validateForm();
      if (Object.keys(formErrors).length > 0) {
        setErrors(formErrors);

        // Her bir kart bilgisi hatası için ayrı toast mesajı göster
        if (formErrors.cardNumber) {
          showToast(`Lütfen ${formErrors.cardNumber}`, 'error');
        }
        if (formErrors.cardHolderName) {
          showToast(`Lütfen ${formErrors.cardHolderName}`, 'error');
        }
        if (formErrors.expiryDate) {
          showToast(`Lütfen ${formErrors.expiryDate}`, 'error');
        }
        if (formErrors.cvc) {
          showToast(`Lütfen ${formErrors.cvc}`, 'error');
        }
        if (formErrors.installment) {
          showToast(`Lütfen ${formErrors.installment}`, 'error');
        }

        return;
      }
    }

    setLoading(true);
    try {
      const referansKodu = location.state?.referansKodu || '';

      if (!customerForm) {
        showToast('Fatura bilgileri bulunamadı. Lütfen adres adımına geri dönün.', 'error');
        setLoading(false);
        return;
      }

      if (
        !billingInfo.ad ||
        !billingInfo.soyad ||
        !billingInfo.adres ||
        !billingInfo.dogumTarihi ||
        !billingInfo.tckimlik ||
        !billingInfo.telefon
      ) {
        showToast('Fatura bilgileri eksik. Lütfen bilgileri düzenleyiniz.', 'error');
        setLoading(false);
        return;
      }

      const tcknDigits = billingInfo.tckimlik.replace(/\D/g, '');
      const phoneDigits = billingInfo.telefon.replace(/\D/g, '');
      const expectedLength = selectedCountry.mobile_number_length;

      if (!validateTCKN(tcknDigits)) {
        showToast('Fatura TC Kimlik bilgisinde sorun var. Lütfen kontrol ediniz.', 'error');
        setLoading(false);
        return;
      }

      if (phoneDigits.length !== expectedLength) {
        showToast(`Telefon numarası ${expectedLength} haneli olmalıdır. Lütfen kontrol ediniz.`, 'error');
        setLoading(false);
        return;
      }

      const updatedCustomerForm = {
        ...customerForm,
        faturaAd: (billingInfo.ad || '').trim(),
        faturaSoyad: (billingInfo.soyad || '').trim(),
        faturaAdres: (billingInfo.adres || '').trim(),
        faturaDogumTarihi: billingInfo.dogumTarihi,
        faturaTckimlik: tcknDigits,
        faturaTelefon: phoneDigits,
        faturaIl: billingInfo.il ?? '',
        faturaIlce: billingInfo.ilce ?? '',
        faturaEmail: billingInfo.email ?? ''
      };

      // EFT/Havale ödeme yöntemi seçiliyse
      if (customerForm.paymentType === 'eft') {
        fbq('track', 'InitiateCheckout');
        try {
          // const discountedTotal = total - eftDiscount;
          const eftPaymentData = {
            musteriForm: updatedCustomerForm,
            // odenecek_tutar: discountedTotal,
            odenecek_tutar: total,
            // orijinal_tutar: total,
            // indirim_tutari: eftDiscount,
            referans_kodu: referansKodu,
            payment_type: 'eft',
            isEftPayment: true
          };

          const response = await processPayment(eftPaymentData, showToast);

          if (response.success) {
            setOrderCreated(true);
            setOrderNumber(response.orderId || 'EFT-' + Date.now());
            setOrderAmount(result.price);

            // EFT siparişi başarıyla oluşturulduğunda share_ref çerezini sil
            document.cookie = "share_ref=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

            setLoading(false);
          } else {
            throw new Error(response.message || 'EFT/Havale sipariş kaydı başarısız');
          }
        } catch (error) {
          setErrors(prev => ({
            ...prev,
            submit: error.message || 'Sipariş oluşturulurken bir hata oluştu'
          }));
          setLoading(false);
        }
        return;
      }

      // Kredi kartı ödemesi için
      // Eğer selectedInstallmentData null ise varsayılan değerleri kullan
      const installmentData = selectedInstallmentData || { count: 1, rate: 0 };

      let finalAmount;
      if (hasSarrafiyeProducts && installmentData.count === 1) {
        // 1. taksitte sarrafiye komisyonu ekle
        const sarrafiyeCommission = total * 0.04;
        finalAmount = total + sarrafiyeCommission;
      } else {
        // Diğer taksitlerde sadece banka komisyonu
        finalAmount = total * (1 + (installmentData.rate / 100));
      }

      const paymentData = {
        kart_numarasi: cardNumber,
        isim: cardHolderName,
        son_kullanma: expiryDate,
        ccv: cvc,
        taksit_sayisi: installmentData.count,
        odenecek_tutar: finalAmount,
        musteriForm: updatedCustomerForm,
        referans_kodu: referansKodu,
      };

      fbq('track', 'Purchase', {
        currency: 'TRY',
        value: total
      });

      const response = await processPayment(paymentData, showToast);

      if (response.success) {
        if (!response.redirect_url) {
          setErrors(prev => ({
            ...prev,
            submit: 'Banka kartlarına taksit yapılamamaktadır.'
          }));
          return;
        }

        // Share_ref çerezini sil
        document.cookie = "share_ref=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

        if (response.redirect_url) {
          window.location.replace(response.redirect_url);
          return;
        } else {
          navigate('/success', {
            state: {
              orderId: response.orderId,
              amount: installmentData.totalAmount || total
            }
          });
        }
      } else {
        // Başarısız ödeme durumunda share_ref çerezini silme
        throw new Error(response.message || 'Ödeme işlemi başarısız');
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        submit: error.message || 'Ödeme işlemi sırasında bir hata oluştu'
      }));
    } finally {
      setLoading(false);
    }
  };

  const defaultInstallments = [
    { count: 1, totalAmount: total },
    { count: 2, totalAmount: total },
    { count: 3, totalAmount: total },
  ];

  // Metin kopyalama fonksiyonu
  const copyToClipboard = (text, label) => {
    try {
      // Yardımcı textarea elementi oluştur
      const textArea = document.createElement('textarea');
      textArea.value = text;
      // Görünmez yap
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);

      // Seç ve kopyala
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        setCopySuccess(`${label} kopyalandı!`);
        setTimeout(() => setCopySuccess(''), 2000);
      } else {
        // execCommand başarısız olursa modern API'yi dene
        navigator.clipboard.writeText(text)
          .then(() => {
            setCopySuccess(`${label} kopyalandı!`);
            setTimeout(() => setCopySuccess(''), 2000);
          })
          .catch(err => {
            setCopySuccess('Kopyalama başarısız!');
            console.error('Kopyalama hatası:', err);
          });
      }
    } catch (err) {
      setCopySuccess('Kopyalama başarısız!');
      console.error('Kopyalama hatası:', err);
    }
  };

  // EFT ödeme bildirimi fonksiyonu
  const handleConfirmEftPayment = async () => {
    try {
      if (!orderNumber) {
        console.error('Sipariş numarası bulunamadı');

        showToast('Sipariş numarası bulunamadı. Lütfen tekrar deneyin.', 'error');
        return;
      }
      setLoading(true);
      const result = await confirmEftPayment(orderNumber, orderAmount, showToast);

      if (result.success) {
        setPaymentState({
          success: true,
          orderId: orderNumber,
          amount: result.price
        });

      } else {
        // Toast mesajı zaten confirmEftPayment içinde gösteriliyor.
      }
    } catch (error) {
      console.error('EFT ödeme bildirimi yapılırken hata:', error);
      showToast('EFT bildirimi yapılırken bir hata oluştu.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // EFT ödeme onaylama için useEffect
  useEffect(() => {
    // orderNumber değeri varsa ve orderCreated true ise
    if (orderNumber && orderCreated && isEftPayment) {
      handleConfirmEftPayment();
    }
  }, [orderNumber, orderCreated, isEftPayment]);

  // Fatura adı ve soyadı değiştiğinde kart sahibi adını otomatik güncelle
  const handlePaymentComplete = () => {
    setIsPaymentCompleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsPaymentCompleteModalOpen(false);
    navigate('/');
  };

  if (loading) return <div className='loader-info'>
    <p className='m-0'>İşlem alınıyor...</p>
    <Loader />
  </div>;
  { error && <p>Hata: {error}</p> }
  { paymentSuccess && <p>Ödeme başarıyla gerçekleşti!</p> }
  return (
    <>
      <div className='payment-title'>
        <h2 className='my-3'>Ödeme Sayfası</h2>

        {/* Fatura Bilgileri Formu - EFT ödemesi ve sipariş oluşturulmuşsa gösterme */}
        {!(isEftPayment && orderCreated) && (
          <div>
            <div>
              <div className="payment-information-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Fatura Bilgileri</h3>
                {isLoggedIn && !isEditingInvoice && (
                  <button
                    onClick={handleEditClick}
                    style={{
                      padding: '5px 10px',
                      fontSize: '0.9rem',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Düzenle
                  </button>
                )}
              </div>
              {
                !isEftPayment ?
                  <div>
                    <p className="invoice-info-text"><strong>Fatura bilgileri</strong> kredi kartı sahibi ile <strong>aynı olmalıdır</strong></p>

                    <p className='invoice-info-text'>Son ekrana gelene kadar ürün fiyatları kurlar ile birlikte <strong>güncellenmektedir.</strong> Son ulaştığınız ekrandaki fiyat geçerlidir.</p>
                  </div>
                  :
                  <div className='invoice-group'>
                    <p className="invoice-info-text"><strong>Ücretin gönderildiği hesap</strong> fatura bilgisi ile <strong>uymalıdır</strong>.
                      Aksi takdirde <strong>siparişiniz iptal edilecektir</strong></p>

                    <p className='invoice-info-text'>Son ekrana gelene kadar ürün fiyatları kurlar ile birlikte <strong>güncellenmektedir.</strong> Son ulaştığınız ekrandaki fiyat <strong>10 dakika</strong> için geçerlidir.</p>

                    <p className='invoice-info-text'><strong>10 dakika</strong> içinde ödeme <strong>gerçekleştirilmezse</strong> fiyatlar <strong>anlık</strong> kurla beraber güncellenecektir</p>

                    {/* <div className="eft-discount-info" style={{
                      backgroundColor: '#e8f5e8',
                      border: '1px solid #4caf50',
                      padding: '15px',
                    }}>
                      <p style={{ margin: '0', color: '#2e7d32', fontWeight: 'bold' }}>
                        🎉 EFT/Havale ile ödeme yaparak %2 indirim kazandınız!
                      </p>
                    </div> */}
                  </div>
              }
            </div>
            <div className="invoice-information">
              <div className="invoice-form">
                {isEditingInvoice ? (
                  <>
                    <div className="d-flex flex-md-row flex-column gap-2">
                      <div className="form-group">
                        <label>Ad</label>
                        <input
                          type="text"
                          name="ad"
                          value={editedInvoiceData.ad || ''}
                          onChange={handleInvoiceChange}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Soyad</label>
                        <input
                          type="text"
                          name="soyad"
                          value={editedInvoiceData.soyad || ''}
                          onChange={handleInvoiceChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    <div className="d-flex flex-md-row flex-column gap-2">
                      <div className='form-group'>
                        <label>TC Kimlik No</label>
                        <input
                          type="text"
                          name="tckimlik"
                          value={editedInvoiceData.tckimlik || ''}
                          onChange={handleInvoiceChange}
                          maxLength="11"
                          className="form-control"
                          style={{ marginBottom: '5px' }}
                        />
                      </div>
                      <div className="form-group">
                        <label>Telefon</label>
                        <div className='d-flex gap-2'>
                          <div>
                            <CountrySelector
                              selectedCountry={selectedCountry}
                              onSelect={(country) => {
                                setSelectedCountry(country);
                                setEditedInvoiceData(prev => ({ ...prev, telefon: '' }));
                              }}
                              countries={countryCodes}
                            />
                          </div>
                          <input
                            type="tel"
                            name="telefon"
                            value={editedInvoiceData.telefon || ''}
                            onChange={handlePhoneChange}
                            className="form-control"
                            placeholder={selectedCountry.mobile_number_length ? `${selectedCountry.mobile_number_length} haneli` : "Telefon"}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='d-flex flex-md-row flex-column gap-2'>
                      <div className="form-group">
                        <label>Doğum Tarihi</label>
                        <input
                          type="date"
                          name="dogumTarihi"
                          value={editedInvoiceData.dogumTarihi || ''}
                          onChange={handleInvoiceChange}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          value={editedInvoiceData.email || ''}
                          onChange={handleInvoiceChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Adres</label>
                      <textarea
                        name="adres"
                        value={editedInvoiceData.adres || ''}
                        onChange={handleInvoiceChange}
                        className="form-control"
                        rows="3"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Ad Soyad</label>
                      <p className="readonly-text">
                        {`${billingInfo.ad || ''} ${billingInfo.soyad || ''}`.trim() || '-'}
                      </p>
                    </div>
                    <div className="form-group">
                      <label>TC Kimlik No</label>
                      <p className="readonly-text">
                        {billingInfo.tckimlik || '-'}
                      </p>
                    </div>
                    <div className="form-group">
                      <label>Telefon</label>
                      <p className="readonly-text">
                        {formatPhoneForDisplay(billingInfo.telefon)}
                      </p>
                    </div>
                    <div className="form-group">
                      <label>Doğum Tarihi</label>
                      <p className="readonly-text">
                        {formatDateForDisplay(billingInfo.dogumTarihi)}
                      </p>
                    </div>
                    {billingInfo.email && (
                      <div className="form-group">
                        <label>Email</label>
                        <p className="readonly-text">{billingInfo.email}</p>
                      </div>
                    )}
                    <div className="form-group">
                      <label>Adres</label>
                      <p className="readonly-text">
                        {billingAddressLine || '-'}
                      </p>
                    </div>
                  </>
                )}
              </div>
              {!isEftPayment && (
                <div className={`payment-page ${isFormActive ? "form-active" : ""}`}>
                  <div className="payment-container">
                    <div className="payment-form">
                      <div className="commission-info">
                        <button onClick={() => setIsOpen(true)}>Komisyon Oranları</button>
                        {commissionRate.length > 0 ? (
                          <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)} contentLabel="Komisyon Oranları" className="ReactModal__Content"
                            overlayClassName="ReactModal__Overlay">
                            <h2>Komisyon Oranları</h2>
                            <button className="close-button" onClick={() => setIsOpen(false)}>
                              &times;</button>
                            <table>
                              <thead>
                                <tr>
                                  <th>Banka</th>
                                  <th>1 Taksit</th>
                                  <th>2 Taksit</th>
                                  <th>3 Taksit</th>
                                </tr>
                              </thead>
                              <tbody>
                                {commissionRate.map((bank, i) => {
                                  // if (bank.name == "Şekerbank CC") return null;

                                  const taksit1 = bank.taksit.find((t) => t.count == 1)?.rate ?? '';
                                  let taksit2 = bank.taksit.find((t) => t.count == 2)?.rate ?? '';
                                  let taksit3 = bank.taksit.find((t) => t.count == 3)?.rate ?? '';
                                  taksit2 = parseFloat(taksit2).toFixed(2)
                                  taksit3 = parseFloat(taksit3).toFixed(2)

                                  return (
                                    <tr key={i}>
                                      <td>{bank.name}</td>
                                      {/* <td>{bank.name}<img className='bank-img' src={bank.image} alt={bank.name} /></td> */}
                                      <td>{taksit1}</td>
                                      <td>{taksit2}%</td>
                                      <td>{taksit3}%</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </Modal>
                        ) : (
                          <p>Komisyon oranı mevcut değil.</p>
                        )}
                      </div>

                      <form onSubmit={handlePaymentSubmit}>
                        <div className="form-group">
                          <label>Kart Numarası:</label>
                          <input
                            type="text"
                            value={cardNumber}
                            onChange={handleCardNumberChange}
                            maxLength="16"
                            placeholder="Kart numaranızı giriniz"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Kart Üzerindeki Ad Soyad:</label>
                          <input
                            type="text"
                            value={cardHolderName}
                            onChange={handleCardHolderNameChange}
                            placeholder="Kartın üzerindeki ad soyad"
                            required
                            disabled
                          />
                        </div>

                        <div className="form-row d-flex gap-1">
                          <div className="small-group d-flex flex-column">
                            <label>Tarihi:</label>
                            <input
                              type="text"
                              value={expiryDate}
                              onChange={handleExpiryDateChange}
                              placeholder="AA/YY"
                              maxLength="5"
                              required
                            />
                          </div>
                          <div className="small-group d-flex flex-column">
                            <label>CVC:</label>
                            <input
                              type="text"
                              value={cvc}
                              onChange={handleCvcChange}
                              placeholder="CVC"
                              maxLength="3"
                              required
                            />
                          </div>
                        </div>

                        {cardNumber.length >= 8 && (
                          <div className="installment-options">
                            <h3>Taksit Seçenekleri</h3>
                            <table className="installment-table">
                              <thead>
                                <tr>
                                  <th>Taksit Sayısı</th>
                                  <th>Aylık Ödeme</th>
                                  <th>Banka Komisyonu</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  // API'den taksit seçenekleri yüklenmediyse varsayılan seçenekleri kullan
                                  const availableInstallments = (installments && installments.length > 0)
                                    ? installments
                                    : [{ count: 1, rate: 0, totalAmount: total }];

                                  return availableInstallments.map((installment) => {
                                    let displayAmount;

                                    if (hasSarrafiyeProducts && installment.count === 1) {
                                      // 1. taksitte sarrafiye komisyonu ekle
                                      const sarrafiyeCommission = total * 0.04;
                                      const adjustedTotal = total + sarrafiyeCommission;
                                      displayAmount = adjustedTotal.toFixed(2);
                                    } else {
                                      // Diğer taksitlerde sadece banka komisyonu
                                      displayAmount = calculateMonthlyPayment(total, installment.count, installment.rate);
                                    }

                                    return (
                                      <tr key={installment.count}>
                                        <td> <label>
                                          <input
                                            type="radio"
                                            name="installment"
                                            value={installment.count}
                                            checked={selectedInstallment === installment.count}
                                            onChange={handleInstallmentChange}
                                          />
                                          {installment.count} Taksit</label>
                                        </td>
                                        <td>
                                          <span>{displayAmount} TL x {installment.count} </span>
                                        </td>
                                        <td>
                                          <span>
                                            {hasSarrafiyeProducts && installment.count === 1 ? '4' : (installment.rate || 0)} %
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  });
                                })()}
                              </tbody>
                            </table>

                            {/* Sarrafiye komisyonu bilgilendirmesi */}
                            {hasSarrafiyeProducts && (
                              <div className="sarrafiye-commission-info" style={{
                                backgroundColor: '#e0f0ffff',
                                border: '1px solid #9fcaf1ff',
                                borderRadius: '5px',
                                padding: '10px',
                                marginTop: '15px',
                                fontSize: '14px',
                                color: '#425A8B'
                              }}>
                                <p style={{ margin: '0', fontWeight: 'bold' }}>
                                  ℹ️ Sarrafiye ürünlerinde 1. taksitte %4 komisyon yansıtılacaktır
                                </p>
                              </div>
                            )}

                            {errors.submit && (
                              <p className="payment-error-message">
                                {errors.submit}
                              </p>
                            )}
                          </div>
                        )}

                        <button type="submit" disabled={loading} className='payment-button'>
                          {loading ? 'İşlem Yapılıyor...' : 'Ödeme Yap'}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div >
          </div >
        )}

        {/* Ödeme Bilgileri Sekmesi */}
        <div className="payment-information-section">
          <div className="">
            {/* EFT veya Kart Bilgileri */}
            {isEftPayment && (
              <div>
                {!orderCreated ? (
                  // Sipariş oluşturulmadan önce
                  <div className='eft-confirm-container'>
                    <p>Siparişinizi oluşturmak için aşağıdaki butona tıklayın. Sonrasında ödeme bilgileriniz görüntülenecektir.</p>
                    <button
                      onClick={handlePaymentSubmit}
                      className="eft-confirm-button"
                      disabled={loading}
                    >
                      {loading ? 'İşlem Yapılıyor...' : 'Siparişi Oluştur'}
                    </button>
                    {errors.submit && (
                      <div className="eft-error-message">
                        <p>{errors.submit}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  // Sipariş oluşturulduktan sonra
                  <div>
                    <h3>EFT/Havale ile Ödeme - Sipariş Onayınız</h3>
                    <p>Siparişiniz başarıyla oluşturuldu. Aşağıdaki banka hesabına ödemenizi yapınız. Ödeme sonrası siparişiniz onaylanarak işleme alınacaktır.</p>

                    <div className="order-details">
                      <p>
                        <strong>Sipariş Numarası:</strong> {orderNumber}
                      </p>
                      <p><strong>Ödenecek Tutar:</strong> {(total).toFixed(2)} TL</p>
                      <p className='order-details-info'>
                        10 Dakika içerisinde ödeme yapmazsanız fiyat güncellenecektir.
                      </p>
                    </div>

                    <div className="bank-account-info">
                      <div>
                        <p><strong>Banka:</strong></p>
                        <p>{eftData ? eftData.bank : 'Yükleniyor...'}</p>
                      </div>
                      <div>
                        <p><strong>IBAN:</strong></p>
                        <p>
                          {eftData ? eftData.iban : 'Yükleniyor...'}
                          <button
                            className="copy-button"
                            onClick={() => copyToClipboard(eftData ? eftData.iban : '', 'IBAN')}
                          >
                            <span>📋</span>
                          </button>
                        </p>
                      </div>
                      <div>
                        <p><strong>Hesap Sahibi:</strong></p>
                        <p>
                          {eftData ? eftData.name : 'Yükleniyor...'}
                          <button
                            className="copy-button"
                            onClick={() => copyToClipboard(eftData ? eftData.name : '', 'Hesap sahibi')}
                          >
                            <span>📋</span>
                          </button>
                        </p>
                      </div>
                      <div>
                        <p><strong>Açıklama:</strong></p>
                        <p>
                          {orderNumber} Nolu Sipariş
                          <button
                            className="copy-button"
                            onClick={() => copyToClipboard(`${orderNumber} Nolu Sipariş`, 'Açıklama')}
                          >
                            <span>📋</span>
                          </button>
                        </p>
                      </div>
                    </div>

                    {copySuccess && <div className="copy-success-message">{copySuccess}</div>}

                    <div className="eft-important-notice">
                      <div className="siparis-no-uyarisi">
                        <p>⚠️ <strong>ÖNEMLİ:</strong> Havale/EFT yaparken açıklama kısmına mutlaka <strong>{orderNumber}</strong> yazınız.</p>
                      </div>
                    </div>

                    <button
                      onClick={handlePaymentComplete}
                      className="payment-complete-button btn btn-outline-primary d-block mx-auto"
                    >
                      Ödemeyi Yaptım
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div >

      <Modal
        isOpen={isPaymentCompleteModalOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Ödeme Bildirimi"
        className="ReactModal__Content"
        overlayClassName="ReactModal__Overlay"
      >
        <div className="payment-complete-modal">
          <h2>Teşekkürler!</h2>
          <p>Ödeme bildiriminiz alınmıştır. Kontroller sağlandıktan sonra siparişiniz onaylanacaktır.</p>
          <button onClick={handleCloseModal} className="modal-close-button">
            Ana Sayfaya Dön
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Payment;